// ============================================================================
// pushPrimingRules (W7-B, E12 / Karte C §Empfehlung d, Punkt 3).
// ----------------------------------------------------------------------------
// Der RUECKFALL fuer die Push-Frage: Wer kein Ereignis erlebt (kein Begleiter,
// der einzieht, kein fertiges Abenteuer), wird trotzdem irgendwann gefragt —
// nach 5 Minuten Nutzung ODER beim dritten Seitenaufruf dieser Sitzung.
//
// Aufbau exakt nach dem Muster von `app/reviewPrompt.ts`: eine REINE Funktion
// mit deps-Injektion, damit die Regel ohne DOM, ohne Uhr und ohne Netz testbar
// ist. Der kopflose Host (`PushPrimingHost.tsx`) liefert nur die Messwerte.
//
// Die harten Grenzen bleiben, wo sie schon sind (Karte C §d Punkt 4):
//   • isStartupPushPromptDone()  — verhindert die ZWEITE Frage ueberhaupt
//   • isProactivePushEnabled()   — der Nutzerschalter in den Einstellungen
// Beide werden hier nur GELESEN (als deps), nie gesetzt. Gesetzt wird der
// Done-Merker ausschliesslich beim Beantworten des Dialogs — und dort VOR dem
// OS-Aufruf (AuthProvider.requestStartupPushPermission).
// ============================================================================

/** Sitzungsbremse: hoechstens EINE Rueckfall-Frage je Browser-Sitzung. */
export const PUSH_PRIMING_SESSION_KEY = 'lorath_push_priming_session';
/** Seitenzaehler dieser Sitzung (sessionStorage, wie REVIEW_PROMPT_SESSION_KEY). */
export const PUSH_PRIMING_PAGEVIEW_KEY = 'lorath_push_priming_pageviews';

/** „5 Minuten Nutzung" (E12). */
export const PUSH_PRIMING_USAGE_MS = 5 * 60 * 1000;
/** „dritter Seitenaufruf je Sitzung" (E12). */
export const PUSH_PRIMING_PAGEVIEW_THRESHOLD = 3;

export type PushPrimingFallbackOutcome =
  /** Die Frage wurde bereits beantwortet — es gibt keine zweite. */
  | 'skipped_done'
  /** Der Nutzer hat den proaktiven Dialog in den Einstellungen abgeschaltet. */
  | 'skipped_disabled'
  /** In dieser Sitzung wurde der Rueckfall schon ausgeloest. */
  | 'skipped_session'
  /** Ruhige Seite (Tuer, erste Szene vor dem ersten Zug) — spaeter pruefen. */
  | 'skipped_quiet'
  /** Weder Zeit noch Seitenzahl erreicht — spaeter noch einmal pruefen. */
  | 'skipped_not_ready'
  /** Jetzt fragen. */
  | 'fire';

// ─── W2-B (Review M2): der Rueckfall stoert die erste Szene nicht ───────────
// Beim Gast-Sofortstart zaehlte schon /onboarding (Status kippt auf 'authed')
// als Aufruf 1, /first-scene als 2 — und der Tipp auf eine Vorlage oeffnete
// /chat/:id als Aufruf 3. Genau dort legte sich die Push-Frage als Modal ueber
// die allererste Szene, bevor der Neuling ein Wort geschrieben hatte.
//   • Onboarding und die Tueren (/first-scene) zaehlen nicht als Seitenaufruf
//     und fragen nie.
//   • Chat-Routen fragen erst nach dem ersten eigenen Zug (dieselbe Regel wie
//     der Live-Gespraech-Hinweis und der Coin-Einstieg, hasFirstExperience).
// Die Ereignis-Fragen (companion_wants_to_write, adventure_planning/_ready)
// sind davon unberuehrt — sie haben ihren Anlass selbst.

const UNCOUNTED_PATHS = [/^\/onboarding(?:\/|$)/, /^\/first-scene(?:\/|$)/];
const CHAT_PATHS = [
  /^\/chat\//,
  /^\/character-chat\//,
  /^\/group-chat\//,
  /^\/table-round-chat\//,
  /^\/table-round-session(?:\/|$)/,
];

/** Zaehlt dieser Pfad als Seitenaufruf fuer den Rueckfall? */
export function countsAsPushPrimingPageview(pathname: string): boolean {
  return !UNCOUNTED_PATHS.some((re) => re.test(pathname));
}

/**
 * Soll der Rueckfall auf diesem Pfad schweigen? Tueren immer, Chats bis zum
 * ersten eigenen Zug. Die Pruefung wird nur verschoben, nicht verbraucht.
 */
export function isPushPrimingQuietPath(pathname: string, firstExperience: boolean): boolean {
  if (!countsAsPushPrimingPageview(pathname)) return true;
  return !firstExperience && CHAT_PATHS.some((re) => re.test(pathname));
}

type SessionLike = Pick<Storage, 'getItem' | 'setItem'> | null | undefined;

export interface PushPrimingFallbackDeps {
  /** isStartupPushPromptDone() — harte Grenze. */
  promptDone: boolean;
  /** isProactivePushEnabled() — Nutzerschalter. */
  proactiveEnabled: boolean;
  /** Millisekunden seit dem Beginn dieser Sitzung. */
  elapsedMs: number;
  /** Seitenaufrufe dieser Sitzung, inklusive des aktuellen. */
  pageviews: number;
  /** Sitzungsspeicher; null = keiner verfuegbar (dann bremst nur der Host). */
  session?: SessionLike;
  /** W2-B: ruhige Seite (isPushPrimingQuietPath) — nicht jetzt, spaeter. */
  quiet?: boolean;
}

function readMarker(session: SessionLike): boolean {
  if (!session) return false;
  try {
    return session.getItem(PUSH_PRIMING_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMarker(session: SessionLike): void {
  if (!session) return;
  try {
    session.setItem(PUSH_PRIMING_SESSION_KEY, '1');
  } catch {
    // Privatmodus: dann bremst in dieser Sitzung nur noch der Done-Merker —
    // und der ist ohnehin die harte Grenze.
  }
}

/**
 * Liest den Seitenzaehler dieser Sitzung. Unlesbare/kaputte Werte gelten als 0
 * (fail-closed: lieber einmal zu spaet fragen als aus Versehen sofort).
 */
export function readPushPrimingPageviews(session: SessionLike): number {
  if (!session) return 0;
  try {
    const raw = session.getItem(PUSH_PRIMING_PAGEVIEW_KEY);
    const value = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

/** Zaehlt einen Seitenaufruf und liefert den neuen Stand. */
export function countPushPrimingPageview(session: SessionLike): number {
  const next = readPushPrimingPageviews(session) + 1;
  if (session) {
    try {
      session.setItem(PUSH_PRIMING_PAGEVIEW_KEY, String(next));
    } catch {
      // Ohne Speicher zaehlt der Host im Arbeitsspeicher weiter.
    }
  }
  return next;
}

/**
 * Die Rueckfall-Regel. Setzt bei 'fire' den Sitzungsmarker, damit dieselbe
 * Sitzung nicht erneut fragt — genau wie `runReviewPrompt` es tut, sobald es
 * eine Antwort gab.
 */
export function runPushPrimingFallback(
  deps: PushPrimingFallbackDeps,
): PushPrimingFallbackOutcome {
  if (deps.promptDone) return 'skipped_done';
  if (!deps.proactiveEnabled) return 'skipped_disabled';
  if (readMarker(deps.session)) return 'skipped_session';
  // Vor der Schwelle: eine ruhige Seite verschiebt nur, sie verbraucht weder
  // den Sitzungsmarker noch die Frage.
  if (deps.quiet) return 'skipped_quiet';

  const byTime = deps.elapsedMs >= PUSH_PRIMING_USAGE_MS;
  const byPages = deps.pageviews >= PUSH_PRIMING_PAGEVIEW_THRESHOLD;
  if (!byTime && !byPages) return 'skipped_not_ready';

  writeMarker(deps.session);
  return 'fire';
}
